import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  IAttachment,
  IUploadingFile,
  ACCEPTED_MIME_TYPES,
  ACCEPTED_EXTENSIONS,
  MAX_FILE_SIZE
} from 'src/app/models/attachment.model';
import { AttachmentService } from 'src/app/services/attachment.service';
import {
  faTrash,
  faFile,
  faFilePdf,
  faFileWord,
  faFilePowerpoint,
  faFileVideo,
  faFileImage,
  faCloudUploadAlt,
  faGripVertical,
  faExclamationCircle,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-incident-attachment',
  templateUrl: './incident-attachment.component.html'
})
export class IncidentAttachmentComponent implements OnInit, OnDestroy, OnChanges {
  static INCIDENTS_BUCKET = 'westhetics-incidents-imgs';
  static INCIDENTS_BUCKET_URL = `https://${IncidentAttachmentComponent.INCIDENTS_BUCKET}.s3.amazonaws.com/`;
  /** Anexos já salvos (vindos do backend) */
  @Input() existingAttachments: IAttachment[] = [];

  /** ID do plano de tratamento (obrigatório para os endpoints) */
  @Input() treatmentPlanId: string = '';

  /** Se o componente está em modo somente-leitura */
  @Input() readOnly = false;

  /** Emite a lista atualizada de anexos sempre que muda */
  @Output() attachmentsChange = new EventEmitter<IAttachment[]>();

  // Ícones
  faTrash = faTrash;
  faFile = faFile;
  faFilePdf = faFilePdf;
  faFileWord = faFileWord;
  faFilePowerpoint = faFilePowerpoint;
  faFileVideo = faFileVideo;
  faFileImage = faFileImage;
  faCloudUploadAlt = faCloudUploadAlt;
  faGripVertical = faGripVertical;
  faExclamationCircle = faExclamationCircle;
  faCheck = faCheck;
  faTimes = faTimes;

  /** Lista consolidada de anexos (salvos) para exibição e reordenação */
  attachments: IAttachment[] = [];

  /** Arquivos em processo de upload */
  uploadingFiles: IUploadingFile[] = [];

  /** Estado de drag-over na zona de upload */
  isDragOver = false;

  /** Extensões aceitas para input file */
  acceptedExtensions = ACCEPTED_EXTENSIONS;

  /** Erros de validação */
  validationErrors: string[] = [];

  constructor(private attachmentService: AttachmentService) {}

  ngOnInit(): void {
    this.setAttachmentsWithUrls();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingAttachments']) {
      this.setAttachmentsWithUrls();
    }
  }

  private setAttachmentsWithUrls(): void {
    this.attachments = [...this.existingAttachments].map(att => ({
      ...att,
      // Backend retorna file_type (snake_case de fileType), frontend usa content_type
      content_type: att.content_type || (att as any).file_type || '',
      file_url: att.file_url || (att.storage_key ? IncidentAttachmentComponent.INCIDENTS_BUCKET_URL + att.storage_key : '')
    })).sort((a, b) => a.attachment_order - b.attachment_order);
  }

  ngOnDestroy(): void {
    // Revogar object URLs de previews
    this.uploadingFiles.forEach(uf => {
      if (uf.previewUrl && uf.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(uf.previewUrl);
      }
    });
  }

  // ─── Drag & Drop de zona de upload ─────────────────────────────────

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.readOnly) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFiles(Array.from(input.files));
      // Reset do input para permitir re-seleção do mesmo arquivo
      input.value = '';
    }
  }

  // ─── Validação e processamento de arquivos ─────────────────────────

  private processFiles(files: File[]): void {
    this.validationErrors = [];

    for (const file of files) {
      // Validar tipo
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        this.validationErrors.push(
          `"${file.name}" — tipo não suportado (${file.type || 'desconhecido'}).`
        );
        continue;
      }

      // Validar tamanho
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        this.validationErrors.push(
          `"${file.name}" — excede 100 MB (${sizeMB} MB).`
        );
        continue;
      }

      // Criar preview para imagens
      let previewUrl: string | null = null;
      if (file.type.startsWith('image/')) {
        previewUrl = URL.createObjectURL(file);
      }

      const uploadingFile: IUploadingFile = {
        file,
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type,
        progress: 0,
        status: 'pending',
        previewUrl
      };

      this.uploadingFiles.push(uploadingFile);
      this.startUpload(uploadingFile);
    }

    // Limpar erros após 6 segundos
    if (this.validationErrors.length > 0) {
      setTimeout(() => {
        this.validationErrors = [];
      }, 6000);
    }
  }

  // ─── Fluxo de upload ───────────────────────────────────────────────

  private async startUpload(uploadingFile: IUploadingFile): Promise<void> {
    if (!this.treatmentPlanId) {
      uploadingFile.status = 'error';
      uploadingFile.errorMessage = 'ID do plano de tratamento não informado.';
      return;
    }

    try {
      uploadingFile.status = 'uploading';

      // 1. Obter presigned URL (endpoint único: POST /incidents/attachments/generate-upload-url)
      const presigned = await this.attachmentService.generateUploadUrl({
        file_name: uploadingFile.fileName,
        content_type: uploadingFile.contentType,
        treatment_plan_id: this.treatmentPlanId
      });

      // 2. Upload para S3 (mesmo padrão de companyService.uploadFileToS3)
      await this.attachmentService.uploadFileToS3(
        presigned.uploadUrl,
        uploadingFile.file
      );
      uploadingFile.progress = 100;

      // 3. Criar attachment local (metadados serão registrados no backend ao salvar o formulário)
      uploadingFile.status = 'done';
      const attachment: IAttachment = {
        id: '',
        file_name: uploadingFile.fileName,
        storage_key: presigned.objectKey,
        file_url: presigned.objectKey ? IncidentAttachmentComponent.INCIDENTS_BUCKET_URL + presigned.objectKey : '',
        file_size: uploadingFile.fileSize,
        content_type: uploadingFile.contentType,
        attachment_order: this.attachments.length
      };
      uploadingFile.attachment = attachment;

      this.attachments.push(attachment);
      this.emitChanges();

      // Remover da lista de uploads após delay visual
      setTimeout(() => {
        this.removeFromUploading(uploadingFile);
      }, 1500);

    } catch (error: any) {
      console.error('Erro no upload:', uploadingFile.fileName, error);
      uploadingFile.status = 'error';
      uploadingFile.errorMessage = error?.message || 'Erro ao fazer upload do arquivo.';
    }
  }

  /** Remove item da lista de uploads em andamento */
  removeFromUploading(uploadingFile: IUploadingFile): void {
    const idx = this.uploadingFiles.indexOf(uploadingFile);
    if (idx > -1) {
      // Revogar preview
      if (uploadingFile.previewUrl && uploadingFile.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(uploadingFile.previewUrl);
      }
      this.uploadingFiles.splice(idx, 1);
    }
  }

  /** Tenta reenviar um arquivo que falhou */
  retryUpload(uploadingFile: IUploadingFile): void {
    uploadingFile.progress = 0;
    uploadingFile.status = 'pending';
    uploadingFile.errorMessage = undefined;
    this.startUpload(uploadingFile);
  }

  // ─── Reordenação via CDK Drag and Drop ─────────────────────────────

  onReorder(event: CdkDragDrop<IAttachment[]>): void {
    if (this.readOnly) return;

    moveItemInArray(this.attachments, event.previousIndex, event.currentIndex);

    // Atualizar attachment_order
    this.attachments.forEach((att, i) => {
      att.attachment_order = i;
    });

    this.emitChanges();
  }

  // ─── Remoção ───────────────────────────────────────────────────────

  async deleteAttachment(attachment: IAttachment): Promise<void> {
    if (this.readOnly || !this.treatmentPlanId) return;

    try {
      // If attachment has no backend id (local temporary file), just remove locally
      if (!attachment.id) {
        this.attachments = this.attachments.filter(a => a !== attachment);
      } else {
        await this.attachmentService.deleteAttachment(this.treatmentPlanId, attachment.id);
        this.attachments = this.attachments.filter(a => a.id !== attachment.id);
      }

      // Reindexar ordem
      this.attachments.forEach((att, i) => {
        att.attachment_order = i;
      });

      this.emitChanges();
    } catch (error) {
      console.error('Erro ao excluir anexo:', error);
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────

  private emitChanges(): void {
    this.attachmentsChange.emit([...this.attachments]);
  }

  /** Retorna o ícone FontAwesome com base no content_type */
  getFileIcon(contentType: string) {
    if (contentType.startsWith('image/')) return this.faFileImage;
    if (contentType === 'application/pdf') return this.faFilePdf;
    if (contentType.includes('word') || contentType.includes('document')) return this.faFileWord;
    if (contentType.includes('powerpoint') || contentType.includes('presentation')) return this.faFilePowerpoint;
    if (contentType.startsWith('video/')) return this.faFileVideo;
    return this.faFile;
  }

  /** Retorna a cor do ícone com base no tipo */
  getFileIconColor(contentType: string): string {
    if (contentType.startsWith('image/')) return 'text-blue-500';
    if (contentType === 'application/pdf') return 'text-red-500';
    if (contentType.includes('word') || contentType.includes('document')) return 'text-blue-700';
    if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'text-orange-500';
    if (contentType.startsWith('video/')) return 'text-purple-500';
    return 'text-gray-500';
  }

  /** Verifica se o tipo é imagem para preview inline */
  isImage(contentType: string): boolean {
    return contentType.startsWith('image/');
  }

  /** Formata tamanho de arquivo */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /** Cor da barra de progresso */
  getProgressColor(uf: IUploadingFile): string {
    if (uf.status === 'error') return 'bg-red-500';
    if (uf.status === 'done') return 'bg-green-500';
    return 'bg-westetics-purple-800';
  }
}
